import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { dxtToLocalServerTime, ErrorModal } from "@dexteel/mesf-core";
import { Grid2 } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
	ColDef,
	GridApi,
	IRowNode,
	RowClassParams,
	RowStyle,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { MillAdjustmentByShift } from "../../../../../adjustments/manager/models/MillAdjustmentByShift";
import { getAdjustmentsByDelay } from "../../../../../adjustments/manager/repositories/AdjustmentManagerRepository";

export const useAdjustmentsByDelay = (
	delayId: number,
	onError?: (error: Error) => void,
) => {
	return useQuery<MillAdjustmentByShift[], Error>({
		queryKey: ["adjustmentsByDelay", delayId],
		queryFn: ({ signal }) => {
			if (delayId) {
				return getAdjustmentsByDelay({ delayId }, signal);
			}
			return [];
		},
		onError: onError,
	});
};

export const AdjustmentsTable = ({
	delayId,
	millAdjustmentId,
	onSelectionChange,
}: {
	delayId: number;
	millAdjustmentId: number | null;
	onSelectionChange: (adjustment: MillAdjustmentByShift | null) => void;
}) => {
	const [filterValue, setFilterValue] = useState("");
	const [error, setError] = useState<string>("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const { data: rows, isLoading } = useAdjustmentsByDelay(delayId, (error) =>
		setError(error.message),
	);

	const getRowStyle = (params: RowClassParams): RowStyle | undefined => {
		if (
			(params.data as MillAdjustmentByShift).DelayId &&
			delayId !== (params.data as MillAdjustmentByShift).DelayId
		) {
			return { background: "#E8C8C8" };
		}
		return undefined;
	};

	const { defaultColDef } = useGridDefinitions({});
	const columns: ColDef[] = [
		{
			headerName: "",
			flex: 1,
			maxWidth: 100,
			headerCheckboxSelection: true,
			checkboxSelection: true,
			sortIndex: 1,
		},
		{
			field: "Timestamp",
			headerName: "Time",
			valueGetter: (params) =>
				dxtToLocalServerTime(
					(params.data as MillAdjustmentByShift)?.Timestamp,
					"MM/dd HH:mm:ss",
				) || "",
			flex: 1,
		},
		{
			field: "Stands",
			headerName: "Stands",
			flex: 1,
		},
		{
			field: "AdjustmentTypeName",
			headerName: "Type",
			flex: 1,
		},
		{
			field: "AdjustmentReasonName",
			headerName: "Reason",
			flex: 1,
		},
	];

	const onFirstDataRendered = useCallback(() => {
		gridApi?.forEachNode((node) => {
			if (node.data.isSelected) {
				node.setSelected(true);
			}
		});
	}, [gridApi, rows]);

	useEffect(() => {
		if (!isLoading) {
			if (millAdjustmentId) {
				let node: IRowNode;

				gridApi?.forEachNode((n) => {
					if (
						(n.data as MillAdjustmentByShift).MillAdjustmentId ===
						millAdjustmentId
					) {
						node = n;
						return true;
					}
				});

				if (node!) node.setSelected(true);
			} else {
				gridApi?.deselectAll();
			}
		}
	}, [gridApi, isLoading, millAdjustmentId]);

	return (
		<>
			<Grid2
				container
				style={{
					padding: "0 0 10px",
					justifyContent: "space-between",
					width: "300vw",
				}}
			>
				<Grid2 size={{ xs: 12 }}>
					<TextFieldSearchFilter
						value={filterValue}
						onChange={(val) => {
							setFilterValue(val);
							gridApi?.setGridOption("quickFilterText", val);
						}}
					/>
				</Grid2>
			</Grid2>
			<div style={{ width: "100%", height: "45vh" }}>
				<AgGridReact
					loading={isLoading}
					rowData={rows}
					getRowId={(params) =>
						`${(params.data as MillAdjustmentByShift).MillAdjustmentId}`
					}
					rowHeight={30}
					headerHeight={25}
					animateRows={true}
					onGridReady={(params) => setGridApi(params.api)}
					columnDefs={columns}
					defaultColDef={defaultColDef}
					onFirstDataRendered={onFirstDataRendered}
					loadingOverlayComponent={CenteredLazyLoading}
					rowSelection="single"
					suppressContextMenu={true}
					getRowStyle={getRowStyle}
					onSelectionChanged={(event) => {
						const selectedRows = event.api.getSelectedRows();
						const adjusment =
							selectedRows[0] as unknown as MillAdjustmentByShift;
						onSelectionChange(adjusment ?? null);
					}}
				/>
			</div>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
